import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

interface DraggableResizablePopupProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  defaultWidth?: number;
  defaultHeight?: number;
  storageKey?: string; // For persisting position
}

const DraggableResizablePopup: React.FC<DraggableResizablePopupProps> = ({
  isOpen,
  onClose: _onClose, // Intentionally unused - users can only close via Cancel button
  title = "Edit Summary",
  children,
  defaultWidth = 600,
  defaultHeight = 500,
  storageKey = "intervention-summary-popup",
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size] = useState({ width: defaultWidth, height: defaultHeight }); // Fixed size, not resizable
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const popupRef = useRef<HTMLDivElement>(null);

  // Load saved position from localStorage
  useEffect(() => {
    if (isOpen && storageKey && typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const { position: savedPos } = JSON.parse(saved);
          // Validate saved position is within viewport
          if (savedPos && typeof savedPos.x === "number" && typeof savedPos.y === "number") {
            const maxX = window.innerWidth - defaultWidth;
            const maxY = window.innerHeight - defaultHeight;
            // Ensure position is within viewport bounds
            const validX = Math.max(0, Math.min(savedPos.x, maxX));
            const validY = Math.max(0, Math.min(savedPos.y, maxY));
            setPosition({ x: validX, y: validY });
          } else {
            // Invalid saved position, center the popup
            setPosition({
              x: (window.innerWidth - defaultWidth) / 2,
              y: (window.innerHeight - defaultHeight) / 2,
            });
          }
        } else {
          // No saved position, center the popup
          setPosition({
            x: (window.innerWidth - defaultWidth) / 2,
            y: (window.innerHeight - defaultHeight) / 2,
          });
        }
      } catch (e) {
        console.error("Error loading popup position:", e);
        // On error, center the popup
        if (typeof window !== "undefined") {
          setPosition({
            x: (window.innerWidth - defaultWidth) / 2,
            y: (window.innerHeight - defaultHeight) / 2,
          });
        }
      }
    }
  }, [isOpen, storageKey, defaultWidth, defaultHeight]);

  // Save position to localStorage
  const savePosition = useCallback(() => {
    if (storageKey) {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ position })
      );
    }
  }, [position, storageKey]);

  useEffect(() => {
    if (isOpen) {
      savePosition();
    }
  }, [position, isOpen, savePosition]);

  // Handle dragging
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).closest(".drag-handle")) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      // Constrain to viewport
      const maxX = window.innerWidth - size.width;
      const maxY = window.innerHeight - size.height;
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  }, [isDragging, dragStart, size]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Ensure popup is visible when opened
  useEffect(() => {
    if (isOpen && typeof window !== "undefined" && popupRef.current) {
      // Force a re-render to ensure visibility
      const popup = popupRef.current;
      if (popup) {
        popup.style.display = "flex";
        popup.style.visibility = "visible";
        popup.style.opacity = "1";
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay - visual only, doesn't block scrolling */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1298, // Below dialog but above most content
          backgroundColor: "rgba(0, 0, 0, 0.05)", // Very light overlay (optional, can be removed)
          pointerEvents: "none", // Don't block any interactions - allows scrolling
        }}
      />
      {/* Popup container */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999, // Below Material-UI Dialog (1300) but above most content
          pointerEvents: "none", // Don't block background scrolling
          overflow: "visible", // Allow dropdowns to overflow
        }}
      >
        <Box
          ref={popupRef}
          onMouseDown={handleMouseDown}
          sx={{
            position: "absolute",
            left: `${position.x}px`,
            top: `${position.y}px`,
            width: `${size.width}px`,
            height: `${size.height}px`,
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            display: "flex",
            flexDirection: "column",
            pointerEvents: "auto",
            overflow: "visible", // Allow dropdown to show outside container
          }}
        >
        {/* Header with drag handle */}
        <Box
          className="drag-handle"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#f5f5f5",
            cursor: "move",
            userSelect: "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DragIndicatorIcon sx={{ color: "#666", fontSize: "20px" }} />
            <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          {/* Close button removed - user can only close via Cancel button */}
        </Box>

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            padding: "20px",
            position: "relative",
            minHeight: 0, // Allow flex shrinking
            display: "flex",
            flexDirection: "column",
            overflow: "hidden", // Clip content but allow portal dropdowns
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto", // Scrollable content
              overflowX: "hidden",
              position: "relative",
            }}
          >
            {children}
          </Box>
        </Box>
        </Box>
      </Box>
    </>
  );
};

export default DraggableResizablePopup;

